import styled, { Breakpoint, css } from 'styled-components';
import { ButtonHTMLAttributes, ComponentType } from 'react';
import { media } from 'src/common/styles/breakpoints';

export interface CleanButtonAttrs
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  as?: JSX.IntrinsicElements | ComponentType<unknown>;
}

type ButtonVariant = 'primary' | 'outline';
type ButtonSize = 'default' | 'xl';

const extractButtonStateThemeProps = (
  variant: ButtonVariant,
  state: 'hover' | 'focus' | 'press' | 'disabled'
) => css`
  background: ${({ theme }) => theme.button[variant]?.[state]?.bg};
  color: ${({ theme }) => theme.button[variant]?.[state]?.color};
  border: ${({ theme }) => theme.button[variant]?.[state]?.border};
  border-color: ${({ theme }) => theme.button[variant]?.[state]?.borderColor};
`;

const extractResponsiveSizeVariantsThemeProps = (
  size: ButtonSize,
  breakpoint: Breakpoint
) => css`
  height: ${({ theme }) => theme.button.size?.[size]?.[breakpoint]?.height};
  padding: ${({ theme }) => theme.button.size?.[size]?.[breakpoint]?.padding};
  font-size: ${({ theme }) =>
    theme.button.size?.[size]?.[breakpoint]?.fontSize};
  line-height: ${({ theme }) =>
    theme.button.size?.[size]?.[breakpoint]?.lineHeight};
`;

// add more breakpoints and better support
const extractSizeVariantsThemeProps = (size: ButtonSize) => css`
  ${extractResponsiveSizeVariantsThemeProps(size, 'default')}

  ${media.w.min.px576(extractResponsiveSizeVariantsThemeProps(size, 'sm'))}
  ${media.w.min.px576(extractResponsiveSizeVariantsThemeProps(size, 'md'))}
  ${media.w.min.px1200(extractResponsiveSizeVariantsThemeProps(size, 'xl'))}
`;

const extractButtonThemeProps = (variant: ButtonVariant) => css`
  background: ${({ theme }) => theme.button[variant]?.default?.bg};
  color: ${({ theme }) => theme.button[variant]?.default?.color};
  border: ${({ theme }) => theme.button[variant]?.default?.border};
  border-radius: ${({ theme }) => theme.button[variant]?.default?.borderRadius};

  font-weight: ${({ theme }) => theme.button[variant]?.default?.fontWeight};
`;

export const CleanButton = styled.button.attrs<CleanButtonAttrs>(
  ({ as, type }) =>
    !as && {
      type: type ?? 'button',
    }
)`
  background: transparent;
  border: 0;
  border-radius: 0;
  color: currentColor;
  margin: 0;
  outline: none;
  padding: 0;
  text-decoration: none;
  white-space: nowrap;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:focus,
  &:active {
    outline: 2px solid ${({ theme }) => theme.colors.focus};
    outline-offset: 1px;
  }
`;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
};

export const Button = styled(CleanButton)<ButtonProps>`
  width: 100%;
  display: flex;
  transition: background 0.2s ease;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;

  ${({ $variant = 'primary', $size = 'default' }) => css`
    ${extractButtonThemeProps($variant)}
    ${extractSizeVariantsThemeProps($size)}

    &:hover {
      ${extractButtonStateThemeProps($variant, 'hover')}
    }

    &:active {
      ${extractButtonStateThemeProps($variant, 'focus')}
    }

    &:disabled {
      opacity: 0.7;
      ${extractButtonStateThemeProps($variant, 'disabled')};
    }
  `}
`;

export const CTAButton = (props: Omit<ButtonProps, '$variant' | '$size'>) => (
  <Button $variant="primary" $size="xl" {...props} />
);

export const SecondaryButton = styled(CTAButton)`
  background-color: transparent;
  height: 3.5rem;
`;
